
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DriverCard } from '../interfaces';
import { DataServiceService } from '../services/data-service.service';
import { environment } from '../../environments/environment';


type EntityType = 'driver' | 'truck' | 'order' | 'trailer' | 'dispatch_order' | 'dispatch' | 'customer';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: WebSocket | undefined;
  private subjects = {
    drivers: new BehaviorSubject<any[]>([]),
    trucks: new BehaviorSubject<any[]>([]),
    orders: new BehaviorSubject<any[]>([]),
    trailers: new BehaviorSubject<any[]>([]),
    dispatch_orders: new BehaviorSubject<any[]>([]),
    daily_dispatchs: new BehaviorSubject<DriverCard[]>([]),
    driver_cards: new BehaviorSubject<DriverCard[]>([]),
    dispatchs: new BehaviorSubject<DriverCard[]>([]),
    customers: new BehaviorSubject<any[]>([])
  };

  private driverCardsSubject: BehaviorSubject<DriverCard[]> | null = null;

  private dataService: DataServiceService | null = null;

  private messageHandlers = {
    // Driver handlers
    driver_created: (data: any) => {
      const currentDrivers = this.subjects.drivers.value;
      const driverExists = currentDrivers.some(driver => driver.driver_id === data.driver_id);
      if (!driverExists) {
        const formattedDriver = {
          ...data,
          name: `${data.first_name} ${data.last_name}`
        };
        const updatedDrivers = [...currentDrivers, formattedDriver];
        this.subjects.drivers.next(updatedDrivers);
        
        // Update related entities that might display driver names
        this.updateRelatedEntities(formattedDriver);
      }
    },
    driver_updated: (data: any) => {
      const currentDrivers = this.subjects.drivers.value;
      const currentTrailers = this.subjects.trailers.value;
      
      // Update drivers
      const updatedDrivers = currentDrivers.map(driver => {
        if (driver.driver_id === data.driver_id) {
          return {
            ...driver,
            ...data
          };
        }
        return driver;
      });
      this.subjects.drivers.next(updatedDrivers);

      // Update trailers if trailer_id changed
      if ('trailer_id' in data) {
        const updatedTrailers = currentTrailers.map(trailer => {
          // Clear old assignments
          if (trailer.driver_name && trailer.trailer_id === data.old_trailer_id) {
            return {
              ...trailer,
              driver_name: null
            };
          }
          // Set new assignment
          if (trailer.trailer_id === data.trailer_id) {
            return {
              ...trailer,
              driver_name: `${data.first_name} ${data.last_name}`,
              location_id: 0  // Set location to 0 when assigned to driver
            };
          }
          return trailer;
        });
        this.subjects.trailers.next(updatedTrailers);
      }
    },
    driver_deleted: (data: any) => this.handleEntityDeleted('drivers', data, 'driver_id'),
    driver_truck_updated: (data: any) => this.handleDriverTruckUpdated(data),

    // Truck handlers
    truck_created: (data: any) => this.handleTruckCreated(data),
    truck_updated: (data: any) => this.handleTruckUpdated(data),
    truck_deleted: (data: any) => this.handleEntityDeleted('trucks', data, 'truck_id'),
    truck_assignment_updated: (data: any) => this.handleTruckAssignmentUpdated(data),

    // Trailer handlers
    trailer_created: (data: any) => this.handleTrailerCreated(data),
    trailer_updated: (data: any) => {
      const currentTrailers = this.subjects.trailers.value;
      const currentDrivers = this.subjects.drivers.value;
      
      const updatedTrailers = currentTrailers.map(trailer => {
        if (trailer.trailer_id === data.trailer_id) {
          // If location_id is 0, find the driver that has this trailer
          const assignedDriver = currentDrivers.find(d => d.trailer_id === data.trailer_id);
          return {
            ...trailer,
            ...data,
            driver_name: assignedDriver ? 
              `${assignedDriver.first_name} ${assignedDriver.last_name}` : 
              null
          };
        }
        return trailer;
      });
      
      this.subjects.trailers.next(updatedTrailers);
    },
    trailer_deleted: (data: any) => this.handleEntityDeleted('trailers', data, 'trailer_id'),
    trailer_assignment_updated: (data: any) => {
      const currentTrailers = this.subjects.trailers.value;
      const updatedTrailers = currentTrailers.map(trailer => {
        if (trailer.trailer_id === data.trailer_id) {
          return {
            ...trailer,
            driver_name: data.location_id === 0 ? data.driver_name : null,
            location_id: data.location_id
          };
        }
        return trailer;
      });
      this.subjects.trailers.next(updatedTrailers);
    },

    // Order handlers
    order_created: (data: any) => this.handleEntityCreated('orders', data),
    order_updated: (data: any) => this.handleEntityUpdated('orders', data, 'order_id'),
    order_deleted: (data: any) => this.handleEntityDeleted('orders', data, 'order_id'),
    order_assigned: (data: any) => this.handleOrderAssigned(data),
    order_removed_from_dispatch: (data: any) => this.handleOrderRemovedFromDispatch(data),

    // Customer handlers
    customer_created: (data: any) => this.handleEntityCreated('customers', data),
    customer_updated: (data: any) => this.handleEntityUpdated('customers', data, 'customer_id'),
    customer_deleted: (data: any) => this.handleEntityDeleted('customers', data, 'customer_id'),

    // Daily dispatch handlers
    dispatch_updated: (data: any) => {
      console.log('Dispatch update received:', data);
      
      const currentCards = this.subjects.dispatchs.value;
      const updatedCards = currentCards.map(card => 
        card.dailyDispatch.id === data.dispatch_id ? 
        {
          ...card,
          dailyDispatch: {
            ...card.dailyDispatch,
            daily_driver_status: data.daily_driver_status,
            notes: data.notes,
            id: data.dispatch_id
          }
        } : card
      );

      this.subjects.dispatchs.next(updatedCards);
    },

    // Driver card handlers (for daily dispatch updates)
    driver_card_updated: (data: any) => {
      const currentCards = this.subjects.driver_cards.value;
      const updatedCards = currentCards.map(card => 
        card.driver.driver_id === data.driver_id ? 
        {
          ...card,
          dailyDispatch: {
            ...card.dailyDispatch,
            daily_driver_status: data.daily_driver_status,
            notes: data.notes
          }
        } : card
      );
      this.subjects.driver_cards.next(updatedCards);
    },

    // Split order handler
    split_order_assigned: (data: any) => {
      
      // Update dispatch_orders
      const currentDispatchOrders = this.subjects.dispatch_orders.value;
      const dispatchOrderExists = currentDispatchOrders.some(order => order.id === data.dispatch_order_id);
      
      if (!dispatchOrderExists) {
        // Format the data to match the dispatchOrders interface
        const formattedDispatchOrder = {
          id: data.dispatch_order_id,
          dispatch_id: data.dispatch_id,
          order_id: data.order_id,
          priority: data.priority,
          scheduled_time: data.scheduled_time,
          notes: data.notes
        };
        
        const updatedDispatchOrders = [...currentDispatchOrders, formattedDispatchOrder];
        this.subjects.dispatch_orders.next(updatedDispatchOrders);
        
        // Update driver cards to reflect the new split order
        this.updateDriverCardsWithSplitOrder(data);
      }
    },
    
    // Misc task handler
    misc_task_assigned: (data: any) => {
      
      // Update dispatch_orders
      const currentDispatchOrders = this.subjects.dispatch_orders.value;
      const dispatchOrderExists = currentDispatchOrders.some(order => order.id === data.dispatch_order_id);
      
      if (!dispatchOrderExists) {
        // Format the data to match the dispatchOrders interface
        const formattedDispatchOrder = {
          id: data.dispatch_order_id,
          dispatch_id: data.dispatch_id,
          order_id: null, // Misc tasks don't have an order_id
          priority: data.priority,
          scheduled_time: data.scheduled_time,
          notes: data.notes
        };
        
        const updatedDispatchOrders = [...currentDispatchOrders, formattedDispatchOrder];
        this.subjects.dispatch_orders.next(updatedDispatchOrders);
        
        // Update driver cards to reflect the new misc task
        this.updateDriverCardsWithMiscTask(data);
      }
    },
    
    // Misc task deleted handler
    misc_task_deleted: (data: any) => {
      
      // Update dispatch_orders
      const currentDispatchOrders = this.subjects.dispatch_orders.value;
      const updatedDispatchOrders = currentDispatchOrders.filter(
        order => order.id !== data.task_id
      );
      
      this.subjects.dispatch_orders.next(updatedDispatchOrders);
      
      // Update driver cards to remove the misc task
      this.removeTaskFromDriverCards(data);
    },
  };

  initializeEntity(entityType: EntityType, subject: BehaviorSubject<any[]>) {
    
    const subjectKey = `${entityType}s` as keyof typeof this.subjects;
    this.subjects[subjectKey] = subject;
    
    if (!this.socket) {
      this.initializeWebSocket();
    }
    
    // Initialize with empty arrays
    subject.next([]);
  }

  private initializeWebSocket() {
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = environment.production
      ? `${environment.wsUrl}` // Production URL
      : 'ws://localhost:8000/ws'; // Development URL
    
    this.socket = new WebSocket(wsUrl);
    this.setupWebSocketEvents();
    
  }

  private setupWebSocketEvents() { 
    if (!this.socket) return;

    this.socket.onmessage = (event) => {
      const { type, data } = JSON.parse(event.data);
      
      const handler = this.messageHandlers[type as keyof typeof this.messageHandlers];
      if (handler) {
        handler(data);
      } else {
        console.log('Unknown message type:', type);
      }
    };
    
  }

  // Generic entity handlers
  private handleEntityCreated(entityType: keyof typeof this.subjects, data: any) {
    const current = this.subjects[entityType].value;
    const exists = current.some(item => item[`${entityType.slice(0, -1)}_id`] === data[`${entityType.slice(0, -1)}_id`]);
    if (!exists) {
      this.subjects[entityType].next([...current, data]);
    }
  }

  private handleEntityUpdated(entityType: keyof typeof this.subjects, data: any, idField: string) {
    const current = this.subjects[entityType].value;
    const updated = current.map(item => 
      item[idField] === data[idField] ? { ...item, ...data } : item
    );
    this.subjects[entityType].next(updated);
  }

  private handleEntityDeleted(entityType: keyof typeof this.subjects, data: any, idField: string) {
    const current = this.subjects[entityType].value;
    const updated = current.filter(item => item[idField] !== data[idField]);
    this.subjects[entityType].next(updated);
  }

  // Update the handleOrderAssigned method
  private handleOrderAssigned(data: any) {
    // Update orders list
    const currentOrders = this.subjects.orders.value;
    const updatedOrders = currentOrders.map(order => 
      order.order_id === data.order_id ? 
      { ...order, assigned_driver_id: data.driver_id, driver_name: data.driver_name } : 
      order
    );
    this.subjects.orders.next(updatedOrders);
    
    // Update driver cards
    if (this.driverCardsSubject) {
      const currentDriverCards = this.driverCardsSubject.value;
      const driverToUpdate = currentDriverCards.find(card => card.driver.driver_id === data.driver_id);
      
      if (driverToUpdate && driverToUpdate.dispatchOrders) {
        const orderExists = driverToUpdate.dispatchOrders.some(order => order.order_id === data.order_id);
        
        if (!orderExists) {
          const orderDetails = currentOrders.find(order => order.order_id === data.order_id);
          
          if (orderDetails) {
            const updatedCards = currentDriverCards.map(card => {
              if (card.driver.driver_id === data.driver_id) {
                return {
                  ...card,
                  dispatchOrders: [...card.dispatchOrders, {
                    id: Date.now(), // Generate a unique ID
                    order_id: data.order_id,
                    priority: String((card.dispatchOrders.length || 0) + 1),
                    scheduled_time: new Date().toISOString(),
                    notes: orderDetails.notes || '',
                    pickup_number: orderDetails.pickup_number || ''
                  }]
                };
              }
              return card;
            });
            
            this.driverCardsSubject.next(updatedCards);
          }
        }
      }
    }
  }

  // Specialized handlers that require custom logic
  private handleTruckCreated(truckData: any) {
    const currentTrucks = this.subjects.trucks.value;
    const currentDrivers = this.subjects.drivers.value;
    
    // Check if truck already exists
    const truckExists = currentTrucks.some(truck => truck.truck_id === truckData.truck_id);
    if (!truckExists) {
        // Add the new truck, including any driver assignment
        const newTruck = {
            ...truckData,
            driver_name: this.getDriverNameForTruck(truckData.truck_id, currentDrivers)
        };
        this.subjects.trucks.next([...currentTrucks, newTruck]);
    }
  }

  private handleTruckUpdated(truckData: any) {
    const currentTrucks = this.subjects.trucks.value;
    const updatedTrucks = currentTrucks.map(truck => 
      truck.truck_id === truckData.truck_id ? {
        ...truck,  // Keep existing truck data
        ...truckData,   // Apply updates
        driver_name: truck.driver_name  // Preserve the driver name
      } : truck
    );
    this.subjects.trucks.next(updatedTrucks);
  }

  private handleTruckAssignmentUpdated(data: any) {
    const currentTrucks = this.subjects.trucks.value;
    const updatedTrucks = currentTrucks.map(truck => {
        if (truck.truck_id === data.old_truck_id) {
            return { ...truck, driver_name: '' };
        }
        if (truck.truck_id === data.new_truck_id) {
            return { ...truck, driver_name: data.driver_name };
        }
        return truck;
    });
    this.subjects.trucks.next(updatedTrucks);

    // Also update the drivers list to maintain consistency
    const currentDrivers = this.subjects.drivers.value;
    const driverToUpdate = currentDrivers.find(d => 
        `${d.first_name} ${d.last_name}` === data.driver_name
    );
    
    if (driverToUpdate) {
        const updatedDrivers = currentDrivers.map(driver => 
            driver.driver_id === driverToUpdate.driver_id 
                ? { ...driver, truck_id: data.new_truck_id }
                : driver
        );
        this.subjects.drivers.next(updatedDrivers);
    }
  }

  private handleTrailerCreated(trailerData: any) {
    const currentTrailers = this.subjects.trailers.value;
    const currentDrivers = this.subjects.drivers.value;

    const trailerExists = currentTrailers.some(trailer => trailer.trailer_id === trailerData.trailer_id);
    if (!trailerExists) {
      const newTrailer = {
        ...trailerData,
        driver_name: this.getDriverNameForTrailer(trailerData.trailer_id, currentDrivers)
      };
      this.subjects.trailers.next([...currentTrailers, newTrailer]);
    }
  }

  handleEntityUpdate(message: { type: EntityType; data: any[] }) {
    const subject = this.subjects[`${message.type}s`];
    if (subject) {
      subject.next(message.data);
    }
  }

  cleanup() {
    this.socket?.close();
  }

  // Add this helper method
  private updateRelatedEntities(driver: any) {
    // Update trucks if this driver is assigned to one
    if (driver.truck_id) {
      const currentTrucks = this.subjects.trucks.value;
      const updatedTrucks = currentTrucks.map(truck => 
        truck.truck_id === driver.truck_id 
          ? { ...truck, driver_name: driver.name }
          : truck
      );
      this.subjects.trucks.next(updatedTrucks);
    }

    // Update trailers if this driver is assigned to one
    if (driver.trailer_id) {
      const currentTrailers = this.subjects.trailers.value;
      const updatedTrailers = currentTrailers.map(trailer => 
        trailer.trailer_id === driver.trailer_id 
          ? { ...trailer, driver_name: driver.name }
          : trailer
      );
      this.subjects.trailers.next(updatedTrailers);
    }
  }

  // Helper method to get driver name
  private getDriverNameForTrailer(trailerId: number, drivers: any[]): string | null {
    const driver = drivers.find(d => d.trailer_id === trailerId);
    return driver ? `${driver.first_name} ${driver.last_name}` : null;
  }

  // Add this helper method to get driver name from drivers list
  private getDriverNameForTruck(truckId: number, drivers: any[]): string | null {
    const driver = drivers.find(d => d.truck_id === truckId);
    return driver ? `${driver.first_name} ${driver.last_name}` : null;
  }

  private handleDriverTruckUpdated(data: any) {
    // Update trucks list
    const currentTrucks = this.subjects.trucks.value;
    const updatedTrucks = currentTrucks.map(truck => {
      if (truck.truck_id === data.truck_id) {
        return {
          ...truck,
          driver_name: data.driver_name
        };
      }
      // Clear driver name from previous truck if applicable
      if (truck.truck_id === data.old_truck_id) {
        return {
          ...truck,
          driver_name: null
        };
      }
      return truck;
    });
    
    this.subjects.trucks.next(updatedTrucks);
  }

  getDriverCardsObservable() {
    return this.subjects.driver_cards.asObservable();
  }

  setDriverCardsSubject(subject: BehaviorSubject<DriverCard[]>) {
    this.driverCardsSubject = subject;
  }

  // Add this new method to handle order removal
  private handleOrderRemovedFromDispatch(data: any) {
    
    // Update orders list if needed
    const currentOrders = this.subjects.orders.value;
    const updatedOrders = currentOrders.map(order => {
      if (order.order_id === data.order_id) {
        // If this is a complete removal (not reassignment)
        if (data.complete_removal) {
          return {
            ...order,
            assigned_driver_id: null,
            driver_name: null,
            pickup_driver_id: null,
            delivery_driver_id: null,
            load_status: 'Unassigned'
          };
        }
        // If it's a removal from one driver but still assigned elsewhere
        return order;
      }
      return order;
    });
    this.subjects.orders.next(updatedOrders);
    
    // Define updatedCards outside the if block
    let updatedCards: DriverCard[] = [];
    
    // Update BOTH driver cards subjects to ensure consistency
    if (this.driverCardsSubject) {
      const currentDriverCards = this.driverCardsSubject.value;
      
      // Find the driver card that had this order
      updatedCards = currentDriverCards.map(card => {
        // If this driver had the order in their dispatch
        const hasOrder = card.dispatchOrders.some(order => order.order_id === data.order_id);
        if (card.driver.driver_id === data.driver_id || hasOrder) {
          
          // Remove the order from this driver's dispatch
          const filteredOrders = card.dispatchOrders.filter(order => 
            order.order_id !== data.order_id
          );
          
          return {
            ...card,
            dispatchOrders: filteredOrders
          };
        }
        return card;
      });
      
      this.driverCardsSubject.next(updatedCards);
      
      // Also update the driver_cards subject in the subjects object
      this.subjects.driver_cards.next(updatedCards);
    }

    if (this.dataService) {
      this.dataService.refreshDriverCardsFromSocket(updatedCards);
    }
  }

  setDataService(service: DataServiceService) {
    this.dataService = service;
  }

  // Add this new method to update driver cards with the split order
  private updateDriverCardsWithSplitOrder(data: any) {
    // Get current driver cards
    const currentCards = this.subjects.dispatchs.value;
    
    // Get current orders to find the pickup number
    const currentOrders = this.subjects.orders.value;
    const orderDetails = currentOrders.find(order => order.order_id === data.order_id);
    const pickupNumber = orderDetails?.pickup_number || '';
    
    // Find the driver card that matches the dispatch_id
    const updatedCards = currentCards.map(card => {
      if (card.dailyDispatch.id === data.dispatch_id) {
        // Create a new dispatch order entry
        const newDispatchOrder = {
          id: data.dispatch_order_id,
          order_id: data.order_id,
          priority: data.priority,
          scheduled_time: data.scheduled_time,
          notes: data.notes,
          pickup_number: pickupNumber
        };
        
        // Add the new dispatch order to the driver's dispatch orders
        return {
          ...card,
          dispatchOrders: [...card.dispatchOrders, newDispatchOrder]
        };
      }
      return card;
    });
    
    // Update the driver cards
    this.subjects.dispatchs.next(updatedCards);
    
    // If we have a separate driver_cards subject, update that too
    if (this.subjects.driver_cards) {
      this.subjects.driver_cards.next(updatedCards);
    }
  }

  // Add this new method to update driver cards with the misc task
  private updateDriverCardsWithMiscTask(data: any) {
    // Get current driver cards
    const currentCards = this.subjects.dispatchs.value;
    
    // Find the driver card that matches the dispatch_id
    const updatedCards = currentCards.map(card => {
      if (card.dailyDispatch.id === data.dispatch_id) {
        // Create a new dispatch order entry for the misc task
        const newDispatchOrder = {
          id: data.dispatch_order_id,
          order_id: null, // Misc tasks don't have an order_id
          priority: data.priority,
          scheduled_time: data.scheduled_time,
          notes: data.notes,
          pickup_number: 'MISC' // Use 'MISC' as the pickup number for misc tasks
        };
        
        // Add the new dispatch order to the driver's dispatch orders
        return {
          ...card,
          dispatchOrders: [...card.dispatchOrders, newDispatchOrder]
        };
      }
      return card;
    });
    
    // Update the driver cards
    this.subjects.dispatchs.next(updatedCards);
    
    // If we have a separate driver_cards subject, update that too
    if (this.subjects.driver_cards) {
      this.subjects.driver_cards.next(updatedCards);
    }
  }

  // Add this new method to remove a task from driver cards
  private removeTaskFromDriverCards(data: any) {
    // Get current driver cards
    const currentCards = this.subjects.dispatchs.value;
    
    // Find the driver card that matches the dispatch_id
    const updatedCards = currentCards.map(card => {
      if (card.dailyDispatch.id === data.dispatch_id) {
        // Remove the task from the driver's dispatch orders
        return {
          ...card,
          dispatchOrders: card.dispatchOrders.filter(
            order => order.id !== data.task_id
          )
        };
      }
      return card;
    });
    
    // Update the driver cards
    this.subjects.dispatchs.next(updatedCards);
    
    // If we have a separate driver_cards subject, update that too
    if (this.subjects.driver_cards) {
      this.subjects.driver_cards.next(updatedCards);
    }
  }

  // Add a method to expose misc task deletion events
  onMiscTaskDeleted(): Observable<any> {
    // Create a subject to emit misc task deleted events
    const miscTaskDeletedSubject = new Subject<any>();
    
    // Add a handler to the messageHandlers object if it doesn't exist
    if (!this.messageHandlers['misc_task_deleted']) {
      this.messageHandlers['misc_task_deleted'] = (data: any) => {
        console.log('Misc task deleted:', data);
        
        // Update dispatch_orders
        const currentDispatchOrders = this.subjects.dispatch_orders.value;
        const updatedDispatchOrders = currentDispatchOrders.filter(
          order => order.id !== data.task_id
        );
        
        this.subjects.dispatch_orders.next(updatedDispatchOrders);
        
        // Update driver cards to remove the misc task
        this.removeTaskFromDriverCards(data);
        
        // Emit the event to subscribers
        miscTaskDeletedSubject.next(data);
      };
    } else {
      // If the handler already exists, wrap it to also emit events
      const originalHandler = this.messageHandlers['misc_task_deleted'];
      this.messageHandlers['misc_task_deleted'] = (data: any) => {
        originalHandler(data);
        miscTaskDeletedSubject.next(data);
      };
    }
    
    return miscTaskDeletedSubject.asObservable();
  }
}
